import { useEffect, useRef } from 'react';
import { AudioTrack as IAudioTrack } from 'twilio-video';
import { useAppState } from '../../state';

interface AudioTrackProps {
  track: IAudioTrack;
}

export default function AudioTrack({ track }: AudioTrackProps) {
  const { activeSinkId, setActiveSinkId } = useAppState();
  const audioEl = useRef<HTMLAudioElement>();

  useEffect(() => {
    audioEl.current = track.attach();
    audioEl.current.setAttribute('data-cy-audio-track-name', track.name);
    document.body.appendChild(audioEl.current);
    return () => track.detach().forEach(el => el.remove());
  }, [track]);

  useEffect(() => {
    const element = audioEl.current;

    if (!element?.setSinkId) {
      return;
    }

    let isDisposed = false;

    const applySinkId = async () => {
      try {
        await element.setSinkId(activeSinkId);
      } catch (error) {
        const err = error as DOMException;

        if (activeSinkId !== 'default' && err?.name === 'NotFoundError') {
          console.warn(`Audio output device "${activeSinkId}" is unavailable. Falling back to default output.`, err);
          if (!isDisposed) {
            setActiveSinkId('default');
          }

          try {
            await element.setSinkId('default');
          } catch (fallbackError) {
            console.warn('Failed to switch audio output back to the default device.', fallbackError);
          }
          return;
        }

        console.warn(`Failed to switch audio output to "${activeSinkId}".`, err);
      }
    };

    void applySinkId();

    return () => {
      isDisposed = true;
    };
  }, [activeSinkId, setActiveSinkId]);

  return null;
}
